import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { User, Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

// Thirugoo Logo Component
const ThirugooLogo = ({ className = "h-8 w-8" }) => (
  <img 
    src="/thirugoo-logo.png" 
    alt="Thirugoo" 
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register } = useAuth();
  const phone = location.state?.phone || '';
  
  const [name, setName] = useState('');
  const [role, setRole] = useState('customer');
  const [loading, setLoading] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    setLoading(true);
    try {
      const user = await register(phone, name.trim(), role);
      toast.success(`Welcome to Thirugoo, ${name}!`);
      if (user.role === 'admin') navigate('/admin');
      else if (user.role === 'driver') navigate('/driver');
      else navigate('/customer');
    } catch (error) {
      toast.error(error.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (!phone) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <ThirugooLogo className="h-12 w-12" />
            <span className="text-3xl font-bold text-gray-900">Thirugoo</span>
          </div>
          <p className="text-gray-600">Complete your registration</p>
        </div>

        <Card className="shadow-xl border-0">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl">Create Account</CardTitle>
            <CardDescription>Phone: +91 {phone}</CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            <form onSubmit={handleRegister} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Enter your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10 h-12 text-lg"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label>I am a</Label>
                <RadioGroup value={role} onValueChange={setRole} className="grid grid-cols-1 gap-3">
                  <Label
                    htmlFor="customer"
                    className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      role === 'customer' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <RadioGroupItem value="customer" id="customer" />
                    <div>
                      <div className="font-semibold">Customer</div>
                      <div className="text-sm text-gray-500">I want to book rides</div>
                    </div>
                  </Label>
                  <Label
                    htmlFor="driver"
                    className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      role === 'driver' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <RadioGroupItem value="driver" id="driver" />
                    <div>
                      <div className="font-semibold">Driver</div>
                      <div className="text-sm text-gray-500">I want to drive for Thirugoo</div>
                    </div>
                  </Label>
                </RadioGroup>
              </div>

              <Button 
                type="submit" 
                className="w-full h-12 bg-amber-600 hover:bg-amber-700 text-lg"
                disabled={loading || !name.trim()}
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Creating Account...</>
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Button 
          variant="ghost" 
          onClick={() => navigate('/login')}
          className="w-full mt-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Login
        </Button>
      </div>
    </div>
  );
};

export default Register;
